import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Avatar,
  CircularProgress,
  Link,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { PropertyField } from '../../system/field';
import { ErrorBoundary } from './ErrorBoundary';
import FallbackError from './FallbackError';
import { formatPhone } from '../../utils';
import { useService } from '../../contexts/ServiceContext';
import { useQueryMemberLicenses } from '../../hooks/useQueryMemberLicenses';
import { useTenant } from 'src/contexts/TenantContext';
import { useAuth } from 'src/contexts/AuthContext';
import EditDeleteButtons from './EditDeleteButtons';
import { ListingResource, useListingField } from '@brightmls/nextgen.lae.flc';

const AVATAR_SIZE = 64; // In pixels

const getPlaceholderImageUrl = (name: string) => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}`;
};

interface ContactCardProps {
  name: PropertyField | string;
  memberKey?: PropertyField | string;
  memberMlsId?: PropertyField | string;
  email?: PropertyField | string;
  phone?: PropertyField | string;
  stateOrProvince?: PropertyField | string;
  hideEdit?: boolean;
  hideDelete?: boolean;
  agentPhoto?: string | null;
  onEdit: () => void;
  onDelete: () => void;
}

export const ContactCard: React.FC<ContactCardProps> = ({
  name,
  memberKey,
  memberMlsId,
  email,
  phone,
  stateOrProvince,
  hideDelete,
  agentPhoto,
  hideEdit,
  onEdit,
  onDelete,
}) => {
  const { findAgentTeam } = useService();
  const { queryMemberLicenses } = useQueryMemberLicenses();
  const { getConfigValue } = useTenant();
  const { session } = useAuth();

  // check for tenant-enabled features
  const enableLicenses = getConfigValue<boolean>('ENABLE_LICENSES');
  const enableTeams = getConfigValue<boolean>('ENABLE_TEAMS');

  const licenseEditUrl: string = getConfigValue('MEMBER_LICENSE_EDIT_URL');

  const getFieldValue = (
    fieldName: PropertyField | string | undefined,
  ): string => {
    // if fieldName exists in PropertyField, we get it from the Property
    if (
      fieldName &&
      Object.values(PropertyField).includes(fieldName as PropertyField)
    ) {
      return useListingField(ListingResource.Property, fieldName, '')?.value;
      // otherwise, if it's a string, we just return that string
      // we do this so set field defaults can disconnect from the property
    } else if (typeof fieldName === 'string') {
      return fieldName;
    }
    return '';
  };

  // Extract values for easier access
  const memberKeyValue = getFieldValue(memberKey);
  const nameValue = getFieldValue(name);
  const memberMlsIdValue = getFieldValue(memberMlsId);
  const emailValue = getFieldValue(email);
  const phoneValue = getFieldValue(phone);
  const stateOrProvinceValue = getFieldValue(stateOrProvince);

  const [teamData, setTeamData] = useState({
    isLoading: false,
    teamName: '',
    teamLead: '',
  });
  const [isLoadingLicense, setIsLoadingLicense] = useState(true);
  const [licenseNumberValue, setLicenseNumberValue] = useState<
    string | undefined
  >(undefined);
  const [licenseExpiry, setLicenseExpiry] = useState<string | undefined>(
    undefined,
  );
  const [editLinkClicked, setEditLinkClicked] = useState(false);

  const licenseLink = useMemo(
    () =>
      licenseEditUrl ? `${licenseEditUrl}?sid=${memberMlsIdValue}` : undefined,
    [memberMlsIdValue],
  );

  const queryLicense = (memberKey: string, stateOrProvince: string) => {
    queryMemberLicenses({
      memberKey: memberKey,
      stateOrProvince: stateOrProvince,
      setLicenseNumber: setLicenseNumberValue,
      setLicenseExpiry,
      setIsLoading: setIsLoadingLicense,
    });
  };

  useEffect(() => {
    queryLicense(memberKeyValue, stateOrProvinceValue);
  }, [memberKeyValue, stateOrProvinceValue]);

  useEffect(() => {
    // if teams is not enabled, do not query team info
    if (!enableTeams) {
      return;
    }

    // if there is no memberKey, contact is not an agent and we don't need to fetch team data
    if (!memberKeyValue.length) {
      return;
    }

    const getTeamData = async () => {
      setTeamData({
        isLoading: true,
        teamName: '',
        teamLead: '',
      });

      try {
        const res = await findAgentTeam(memberKeyValue);
        setTeamData({
          isLoading: false,
          teamName: res?.teamName ? res?.teamName : '',
          teamLead: res?.teamLeadAgentName ? res.teamLeadAgentName : '',
        });
      } catch (error) {
        console.error(`Error retrieving team data for ${nameValue}`, error);
      }
    };
    getTeamData();
  }, [memberKeyValue, nameValue, findAgentTeam]);

  const licenseLinkUI = (content: string | undefined) => (
    <Link
      href={licenseLink}
      sx={{ color: 'secondary.medium' }}
      underline="always"
      color={'secondary.medium'}
      target="_blank"
      onClick={() => setEditLinkClicked(true)}
    >
      {content}
    </Link>
  );

  const shouldShowLink =
    licenseLink && memberMlsIdValue === session?.user.memberMlsId;

  const maybeLicenseNumber = licenseNumberValue ?? 'Unknown';

  const licenseNumberLink = licenseLinkUI(maybeLicenseNumber);

  const licenseExpiryLink = licenseLinkUI(licenseExpiry);

  const licenseNumberMaybeLink =
    memberMlsIdValue && shouldShowLink ? licenseNumberLink : maybeLicenseNumber;

  const licenseExpiryMaybeLink =
    memberMlsIdValue && shouldShowLink ? licenseExpiryLink : licenseExpiry;

  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: '4px',
          mb: 3,
          pb: 2.5,
          pt: 4,
          px: 3,
        }}
      >
        <Box display="flex" alignItems="flex-start">
          {!(emailValue && phoneValue) && (
            <Avatar
              src={agentPhoto ?? getPlaceholderImageUrl(nameValue)}
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                height: AVATAR_SIZE,
                mr: 2,
                width: AVATAR_SIZE,
              }}
            />
          )}
          <Box flexGrow={1}>
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '18px',
                lineHeight: '24px',
              }}
            >
              {nameValue}
            </Typography>

            <Box sx={{ mt: 1.5 }}>
              {enableLicenses && (
                <>
                  {isLoadingLicense && <CircularProgress size={20} />}
                  {!isLoadingLicense &&
                    memberKeyValue && ( // if memberKeyValue is undefined, contact is not an agent and we should hide license info
                      <Typography variant="body1">
                        License Number: {licenseNumberMaybeLink}
                      </Typography>
                    )}
                  {!isLoadingLicense && licenseNumberValue && (
                    <Typography variant="body1">
                      License Expiration: {licenseExpiryMaybeLink}
                    </Typography>
                  )}
                </>
              )}
              {emailValue && (
                <Typography variant="body1">
                  Email Address: {emailValue}
                </Typography>
              )}
              {phoneValue && (
                <Typography variant="body1">
                  Phone Number: {formatPhone(phoneValue)}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              {teamData.isLoading && <CircularProgress size={20} />}
              {teamData.teamName && (
                <Typography
                  variant="body2"
                  sx={{ fontSize: 14, color: 'text.secondary' }}
                >
                  {teamData.teamName}
                </Typography>
              )}
              {teamData.teamLead && (
                <Typography
                  variant="body2"
                  sx={{ fontSize: 14, color: 'text.secondary' }}
                >
                  Team Lead: {teamData.teamLead}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        {(!hideEdit || !hideDelete) && (
          <Divider sx={{ my: 2, backgroundColor: 'grey.200' }} />
        )}
        {(!hideEdit || !hideDelete) && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
            }}
          >
            <EditDeleteButtons
              props={{
                editCallback: onEdit,
                deleteCallback: onDelete,
                otherCallback: () => {
                  queryLicense(memberKeyValue, stateOrProvinceValue);
                },
                hideEdit,
                hideDelete,
                hideOther: !editLinkClicked,
                otherText: 'Refresh',
                otherIcon: <Refresh />,
              }}
            />
          </Box>
        )}
      </Box>
    </ErrorBoundary>
  );
};
